import React, { useEffect, useState } from 'react';
import { CheckCircle, Circle, LoaderCircle } from 'lucide-react';
import { useNavigate, useParams } from 'react-router-dom';
import { useStore } from 'context/StoreContext';
import { CostDocument } from 'dtos/costs/cost-document.dto';
import { CostDocumentService } from 'services/costs/cost-document.service';

const steps = ['Receipt uploaded', 'Reading receipt', 'Matching products', 'Preparing review'];

export default function ProcessingStatusPage() {
  const { documentId } = useParams();
  const { org, currentStore } = useStore();
  const navigate = useNavigate();
  const [document, setDocument] = useState<CostDocument | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [retrying, setRetrying] = useState(false);

  useEffect(() => {
    if (!org?.id || !currentStore?.id || !documentId) return;
    let active = true;
    const poll = async () => {
      try {
        const result = await CostDocumentService.get(org.id, currentStore.id, documentId);
        if (!active) return;
        setDocument(result);
        if (result.status === 'APPLIED') navigate('/costs', { replace: true });
        if (result.status === 'NEEDS_REVIEW' || result.status === 'READY') navigate(`/costs/review/${documentId}`, { replace: true });
      } catch (pollError: any) {
        if (active) setError(pollError?.message ?? 'Unable to check receipt status.');
      }
    };
    void poll();
    const timer = window.setInterval(() => void poll(), 4000);
    return () => { active = false; window.clearInterval(timer); };
  }, [org?.id, currentStore?.id, documentId, navigate]);

  const failed = document?.status === 'FAILED';
  return (
    <div className="mx-auto max-w-xl space-y-5">
      <h1 className="text-xl font-bold text-gray-900">Processing Receipt</h1>
      {failed ? <>
        <p className="text-sm text-red-600">{document.error_message || 'Receipt processing failed.'}</p>
        {!!document.file_urls?.length && <div className="space-y-2 rounded-lg border border-gray-200 bg-white p-4"><p className="text-sm font-semibold text-gray-900">Original receipt</p>{document.file_urls.map((url, index) => <a key={url} href={url} target="_blank" rel="noreferrer" className="block text-sm text-blue-600">View receipt page {index + 1}</a>)}</div>}
        <div className="flex gap-2"><button type="button" className="rounded-md bg-blue-600 px-3 py-2 text-sm font-medium text-white" disabled={retrying} onClick={async () => { setRetrying(true); setError(null); try { const retried = await CostDocumentService.retry(org?.id ?? '', currentStore?.id ?? '', documentId ?? ''); setDocument(retried); } catch (retryError: any) { setError(retryError?.message ?? 'Unable to retry receipt.'); } finally { setRetrying(false); } }}>Try Again</button><button type="button" className="rounded-md border border-gray-300 px-3 py-2 text-sm font-medium text-gray-700" onClick={() => navigate('/costs')}>Back to Costs</button></div>
      </> : <>
        <div className="space-y-3 rounded-lg border border-gray-200 bg-white p-5">
          {steps.map((step, index) => {
            const activeIndex = document?.status === 'PROCESSING' ? 1 : 3;
            const complete = index < activeIndex;
            const active = index === activeIndex;
            return <div key={step} className="flex items-center gap-3 text-sm">
              {complete ? <CheckCircle size={18} className="text-green-600" /> : active ? <LoaderCircle size={18} className="animate-spin text-blue-600" /> : <Circle size={18} className="text-gray-300" />}
              <span className={active ? 'font-medium text-gray-900' : 'text-gray-500'}>{step}{active && '...'}</span>
            </div>;
          })}
        </div>
        <p className="text-sm text-gray-500">You can leave this screen and return from Pending Reviews when processing is complete.</p>
      </>}
      {error && <p className="text-sm text-red-600">{error}</p>}
    </div>
  );
}
